/**
 * THE EMAILS WITH ONE HOUSEHOLD (ops #76 O6, `docs/plan-o6-o9-emails.md` section 3.7): every email ops
 * composed to its people, newest first, each with what it was, who it went to, when, and where it
 * stands (`EmailRows`), read from `GET /api/messages/emails/client/:id` as the page opens, as the texts
 * are. Its own panel beside the texts, never inside them: the thread stays texts only. No subject, since
 * this read asks for no recent sign-in and a subject names an invoice's figure; one about an invoice
 * opens it, behind that invoice's own sign-in.
 *
 * AND THE OFFICE'S OWN EMAIL (ops #84 phase 5, `docs/plan-client-workplace.md` section 8): "Write an
 * email" opens the composer on the email, its switch offering a text beside it, to the household's
 * people with an address. Ops composes and records it; home delivers it from home@ and reports back,
 * and the list is read again once it is written, so it stands at the top waiting to go. A text sent
 * from here is the thread's, and the page is told (`onTextSent`) so the thread reads again.
 *
 * A Worker older than the door answers 404, and the panel is then not drawn at all rather than drawn
 * empty, since empty would say no email ever went.
 */
import { useEffect, useState } from 'react';
import { ApiError, getHouseholdEmails, type HouseholdEmailsPage } from '../api.ts';
import { Composer, type ComposerPerson } from '../screens/Composer.tsx';
import { EmailRows } from './EmailRows.tsx';
import { Button } from './Field.tsx';
import { SidePanel } from './ListView.tsx';
import { Busy, ErrorNotice, SavedNotice } from './Notice.tsx';

type Read =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly page: HouseholdEmailsPage }
  | { readonly kind: 'absent' }
  | { readonly kind: 'failed'; readonly message: string };

export function HouseholdEmails({
  clientId,
  people = [],
  onTextSent,
}: {
  clientId: string;
  /** The household's people, of whom those with an address may be emailed from here. */
  people?: readonly ComposerPerson[];
  /** Told when a text goes from here instead, so the thread beside it reads again. */
  onTextSent?: () => void;
}) {
  const [read, setRead] = useState<Read>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [composing, setComposing] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const emailable = people.filter((person) => person.email !== null && person.email !== undefined);

  useEffect(() => {
    let live = true;
    setRead({ kind: 'loading' });
    getHouseholdEmails(clientId)
      .then((page) => {
        if (live) setRead({ kind: 'loaded', page });
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        if (thrown instanceof ApiError && thrown.status === 404) {
          setRead({ kind: 'absent' });
          return;
        }
        setRead({
          kind: 'failed',
          message: thrown instanceof ApiError ? thrown.message : 'The emails did not load.',
        });
      });
    return () => {
      live = false;
    };
  }, [clientId, attempt]);

  if (read.kind === 'absent') return null;
  return (
    <SidePanel title="Emails">
      {said ? <SavedNotice>{said}</SavedNotice> : null}
      {read.kind === 'loading' ? (
        <Busy what="the emails" />
      ) : read.kind === 'failed' ? (
        <ErrorNotice
          title="The emails did not load"
          message={read.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : read.page.emails.length === 0 ? (
        <p className="text-sm text-navy/70">No email to this household yet.</p>
      ) : (
        <>
          <EmailRows emails={read.page.emails} invoiceLinks />
          {read.page.more ? (
            <p className="text-xs text-navy/60">
              {`The newest ${read.page.limit} are shown; older emails are kept.`}
            </p>
          ) : null}
        </>
      )}
      {composing ? (
        <Composer
          people={people}
          channels={['email', 'sms']}
          onSent={(sentence, channel) => {
            setComposing(false);
            setSaid(sentence);
            if (channel === 'email') setAttempt((n) => n + 1);
            else onTextSent?.();
          }}
          onCancel={() => setComposing(false)}
        />
      ) : emailable.length > 0 ? (
        <div className="mt-2">
          <Button
            tone="quiet"
            onClick={() => {
              setSaid(null);
              setComposing(true);
            }}
          >
            Write an email
          </Button>
        </div>
      ) : people.length > 0 ? (
        <p className="mt-2 text-xs text-navy/60">
          Nobody on this household has an email address on file, so nothing is emailed from here.
        </p>
      ) : null}
    </SidePanel>
  );
}
